import { Helmet } from "react-helmet-async";

const BASE = "https://pqgajztsmyaylotjojfr.supabase.co/functions/v1/public-api";

const endpoints: Array<{ method: string; path: string; desc: string; scope: string; body?: string }> = [
  { method: "GET",  path: "/leads",                desc: "Alle Leads (limit, max 200)", scope: "read" },
  { method: "POST", path: "/leads",                desc: "Neuen Lead erstellen", scope: "write", body: `{ "contact_email": "max@example.com", "contact_name": "Max", "event_type": "Hochzeit", "event_date": "2026-08-15" }` },
  { method: "GET",  path: "/leads/{id}",           desc: "Einzelnen Lead abrufen", scope: "read" },
  { method: "PUT",  path: "/leads/{id}/status",    desc: "Lead-Status ändern", scope: "write", body: `{ "status": "qualified" }` },
  { method: "GET",  path: "/bookings",             desc: "Alle Buchungen", scope: "read" },
  { method: "GET",  path: "/bookings/{id}",        desc: "Einzelne Buchung", scope: "read" },
  { method: "POST", path: "/bookings",             desc: "Neue Buchung", scope: "write", body: `{ "event_date": "2026-08-15T16:00:00Z", "account_id": "uuid", "title": "Hochzeit" }` },
  { method: "GET",  path: "/bookings/availability?date=2026-08-15", desc: "Verfügbarkeit prüfen", scope: "read" },
  { method: "GET",  path: "/contacts",             desc: "Alle Kunden", scope: "read" },
  { method: "POST", path: "/contacts",             desc: "Neuen Kunden anlegen", scope: "write", body: `{ "display_name": "Max Mustermann", "contact_email": "max@example.com" }` },
];

const examples = {
  js: `const res = await fetch("${BASE}/leads", {
  headers: { "X-API-Key": "ssb_xxxx_xxxxxxx" }
});
const { data } = await res.json();`,
  py: `import requests
r = requests.get("${BASE}/leads",
  headers={"X-API-Key": "ssb_xxxx_xxxxxxx"})
print(r.json())`,
  php: `<?php
$ch = curl_init("${BASE}/leads");
curl_setopt($ch, CURLOPT_HTTPHEADER, ["X-API-Key: ssb_xxxx_xxxxxxx"]);
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
echo curl_exec($ch);`,
};

const webhookEvents = [
  { name: "on_lead_created", desc: "Neuer Lead eingegangen" },
  { name: "on_booking_confirmed", desc: "Buchung bestätigt" },
  { name: "on_payment_received", desc: "Zahlung eingegangen" },
  { name: "on_contract_signed", desc: "Vertrag unterzeichnet" },
  { name: "on_event_completed", desc: "Event abgeschlossen" },
];

export default function ApiDocsPage() {
  return (
    <>
      <Helmet>
        <title>API-Dokumentation | SnapShotBox</title>
        <meta name="description" content="REST-API und Webhooks für SnapShotBox; Integrationen mit externen Tools wie Zapier, Make oder eigenen Systemen." />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <main className="min-h-screen bg-background">
        <div className="max-w-4xl mx-auto px-6 py-12 space-y-10">
          <header>
            <h1 className="text-4xl font-bold mb-2">SnapShotBox API</h1>
            <p className="text-muted-foreground">REST-API v1 für Integrationen mit externen Tools.</p>
          </header>

          <section>
            <h2 className="text-2xl font-semibold mb-3">Authentifizierung</h2>
            <p className="text-sm mb-3">Alle Endpoints erwarten einen API-Key als <code className="bg-muted px-1 rounded">X-API-Key</code> Header oder als Bearer-Token.</p>
            <pre className="bg-muted rounded p-3 text-xs overflow-auto">X-API-Key: ssb_&lt;prefix&gt;_&lt;secret&gt;</pre>
            <p className="text-sm mt-3">API-Keys werden im Admin-Bereich erstellt. Rate-Limit: <strong>1000 Anfragen pro Stunde</strong> (konfigurierbar pro Key).</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">Base URL</h2>
            <pre className="bg-muted rounded p-3 text-xs overflow-auto">{BASE}</pre>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">Endpoints</h2>
            <div className="space-y-3">
              {endpoints.map((e) => (
                <div key={`${e.method}${e.path}`} className="border rounded-lg p-4">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className={`px-2 py-1 rounded text-xs font-mono font-bold ${
                      e.method === "GET" ? "bg-blue-100 text-blue-900" :
                      e.method === "POST" ? "bg-green-100 text-green-900" :
                      e.method === "PUT" ? "bg-amber-100 text-amber-900" : "bg-red-100 text-red-900"
                    }`}>{e.method}</span>
                    <code className="font-mono text-sm">{e.path}</code>
                    <span className="text-xs text-muted-foreground ml-auto">scope: {e.scope}</span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-2">{e.desc}</p>
                  {e.body && <pre className="bg-muted rounded p-2 text-xs mt-2 overflow-auto">{e.body}</pre>}
                </div>
              ))}
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">Beispiel-Code</h2>
            <div className="space-y-4">
              <div>
                <h3 className="font-medium mb-2">JavaScript / Node.js</h3>
                <pre className="bg-muted rounded p-3 text-xs overflow-auto">{examples.js}</pre>
              </div>
              <div>
                <h3 className="font-medium mb-2">Python</h3>
                <pre className="bg-muted rounded p-3 text-xs overflow-auto">{examples.py}</pre>
              </div>
              <div>
                <h3 className="font-medium mb-2">PHP</h3>
                <pre className="bg-muted rounded p-3 text-xs overflow-auto">{examples.php}</pre>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">Webhooks</h2>
            <p className="text-sm mb-3">Webhooks senden ein POST an deine URL, sobald ein Event ausgelöst wird. Die Auslieferung wird per HMAC-SHA256 signiert:</p>
            <pre className="bg-muted rounded p-3 text-xs overflow-auto">X-SnapShotBox-Event: on_lead_created
X-SnapShotBox-Signature: sha256=&lt;hex&gt;</pre>
            <p className="text-sm mt-3 mb-2">Die Signatur prüfst du gegen dein Webhook-Secret und den rohen Body:</p>
            <pre className="bg-muted rounded p-3 text-xs overflow-auto">{`// Node.js
const sig = crypto.createHmac("sha256", SECRET).update(rawBody).digest("hex");
if (sig !== req.headers["x-snapshotbox-signature"].replace("sha256=", "")) {
  return res.status(401).end();
}`}</pre>
            <h3 className="font-medium mt-4 mb-2">Verfügbare Events</h3>
            <ul className="text-sm space-y-1">
              {webhookEvents.map(e => (
                <li key={e.name}><code className="bg-muted px-1 rounded">{e.name}</code>; {e.desc}</li>
              ))}
            </ul>
            <p className="text-xs text-muted-foreground mt-3">Retry-Strategie: bei Fehler werden 5 Versuche mit exponentiellem Backoff unternommen (1 min, 5 min, 30 min, 2 h, 12 h), danach landet die Auslieferung im Dead-Letter-Queue.</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">Fehler-Codes</h2>
            <ul className="text-sm space-y-1">
              <li><code>200</code> OK · <code>201</code> Created</li>
              <li><code>400</code> Bad Request; ungültige Parameter</li>
              <li><code>401</code> Unauthorized; API-Key fehlt oder ungültig</li>
              <li><code>403</code> Forbidden; fehlender Scope</li>
              <li><code>404</code> Not Found</li>
              <li><code>429</code> Rate Limit überschritten</li>
              <li><code>500</code> Server Error</li>
            </ul>
          </section>
        </div>
      </main>
    </>
  );
}
